import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PublicAccommodationItem {
  id: string;
  title: string;
  description_short: string | null;
  beds: number | null;
  guest_capacity: number | null;
  price_per_night_text: string | null;
  producer_id: string;
  producer_name?: string;
  place_name?: string;
}

const ACCOMMODATION_SELECT =
  "id, title, description_short, beds, guest_capacity, price_per_night_text, producer_id";

/**
 * Fetch public accommodations for a set of producer IDs.
 * Used by Place and Area pages.
 */
export function usePublicAccommodationsByProducers(
  producerIds: string[] | undefined,
  maxItems = 3,
) {
  return useQuery<PublicAccommodationItem[]>({
    queryKey: ["public-accommodations-by-producers", producerIds, maxItems],
    enabled: Boolean(producerIds && producerIds.length > 0),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!producerIds || producerIds.length === 0) return [];
      const { data, error } = await supabase
        .from("producer_accommodations")
        .select(ACCOMMODATION_SELECT)
        .in("producer_id", producerIds)
        .eq("status", "public")
        .order("created_at", { ascending: false })
        .limit(maxItems);
      if (error) throw error;
      return (data ?? []) as PublicAccommodationItem[];
    },
  });
}

/**
 * Fetch a small curated set of public accommodations for the landing page.
 */
export function useLandingAccommodations(maxItems = 3) {
  return useQuery<(PublicAccommodationItem & { producer_name: string })[]>({
    queryKey: ["landing-accommodations", maxItems],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("producer_accommodations")
        .select(`${ACCOMMODATION_SELECT}, producers!inner(name)`)
        .eq("status", "public")
        .order("created_at", { ascending: false })
        .limit(maxItems);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        ...row,
        producer_name: row.producers?.name ?? "",
        producers: undefined,
      }));
    },
  });
}
